import { toast } from "sonner";
import env from "./env";

export enum ApiEndpoint {
  bookings = "/public/bookings",
  availability = "/public/availability",
  slots = "/public/slots",  
  upload = "/public/upload",
  pages = "/public/pages",
}

const getUri = (endpoint: ApiEndpoint | string, params: any = {}) => {
  const baseUrl = env.API_URL;
  const uri = new URL(`${baseUrl}${endpoint}`);
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams();
    for (const key in params) {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((item) => searchParams.append(key, item));
      } else if (value) {
        searchParams.append(key, value);
      }
    }
    uri.search = new URLSearchParams(searchParams).toString();
  }
  return uri.toString();
};

const handleResponse = async (response: Response) => {
  if (response.status === 401 && window.location.pathname !== "/login") {
    toast.error("Session timeout", { position: "bottom-center" });
    return;
  }
  const json: any = await response.json();
  if (!response.ok) {
    throw new Error(json.message);
  }
  return json;
};

class ApiService {
  private cache = new Map<string, any>();

  async list(endpoint: ApiEndpoint | string, params: any = {}): Promise<any> {
    try {
      const uri = getUri(endpoint, params);

      // Check if response exists in cache
      if (this.cache.has(uri)) {
        return this.cache.get(uri);
      }

      const response = await fetch(uri, {
        method: "GET",
      });
      const data = await handleResponse(response);

      // Store response in cache
      if (data) {
        this.cache.set(uri, data);
      }

      return data;
    } catch (error) {
      throw error;
    }
  }

  async get(endpoint: ApiEndpoint, id: string | number | null): Promise<any> {
    try {
      const response = await fetch(`${env.API_URL}${endpoint}/${id}`, {
        method: "GET",
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async post(endpoint: ApiEndpoint | string, data: any): Promise<any> {
    try {
      const response = await fetch(`${env.API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify(data),
      });
      this.cache.clear();
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async put(
    endpoint: ApiEndpoint,
    id: string | number | null,
    data: any
  ): Promise<any> {
    try {
      const response = await fetch(
        `${env.API_URL}${endpoint}${id ? "/" + id : ""}`,
        {
          method: "PUT",
          headers: {
            "Content-type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      this.cache.clear();
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async patch(
    endpoint: ApiEndpoint,
    id: string | number,
    data: any
  ): Promise<any> {
    try {
      const response = await fetch(
        `${env.API_URL}${endpoint}${id ? "/" + id : ""}`,
        {
          method: "PATCH",
          headers: {
            "Content-type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );
      this.cache.clear();
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async delete(
    endpoint: ApiEndpoint,
    id?: string | number,
    params?: any
  ): Promise<any> {
    const path = id ? `${endpoint}/${id}` : endpoint;
    const uri = getUri(path, params);
    try {
      const response = await fetch(uri, {
        method: "DELETE",
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async upload(
    endpoint: ApiEndpoint | string,
    file: File,
    params: any = {}
  ): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const uri = getUri(endpoint, params);
      const response = await fetch(uri, {
        method: "POST",
        body: formData,
      });
      return await handleResponse(response);
    } catch (error) {
      throw error;
    }
  }
}

export const apiService = new ApiService();
